import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import MagicButton from 'components/MagicButton/MagicButton';
import TextInputField from 'components/TextInputField';
import { AssetUrls } from 'config/assets';
import { showError, showSuccess } from 'utils/toastNotifications';
import {
  useRequestEmailChangeMutation,
  useGetEmailChangeStatusQuery,
  useResendEmailVerificationMutation,
  useCancelEmailChangeMutation
} from 'store/services/auth/authApi';
import './EmailChangeModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currentEmail: string;
}

const EmailChangeModal: React.FC<Props> = ({ isOpen, onClose, currentEmail }) => {
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // API hooks
  const [requestEmailChange, { isLoading: isRequesting }] = useRequestEmailChangeMutation();
  const { data: emailChangeStatus, refetch: refetchStatus } = useGetEmailChangeStatusQuery(undefined, {
    skip: !isOpen,
  });
  const [resendVerification, { isLoading: isResending }] = useResendEmailVerificationMutation();
  const [cancelEmailChange, { isLoading: isCancelling }] = useCancelEmailChangeMutation();

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNewEmail('');
      setEmailError(false);
      setConfirmationVisible(false);
      setShowConfirmDialog(false);
    } else {
      refetchStatus();
    }
  }, [isOpen, refetchStatus]);

  // Auto-close after successful submission
  useEffect(() => {
    if (confirmationVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [confirmationVisible, onClose]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[\w+.-]+@([\w-]+\.)+[\w-]{2,4}$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    const trimmedEmail = newEmail.trim();

    if (!validateEmail(trimmedEmail)) {
      setEmailError(true);
      showError('Please enter a valid email address');
      return;
    }

    if (trimmedEmail.toLowerCase() === currentEmail.toLowerCase()) {
      setEmailError(true);
      showError('New email must be different from current email');
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleConfirmChange = async () => {
    try {
      await requestEmailChange({ newEmail: newEmail.trim() }).unwrap();
      setConfirmationVisible(true);
      showSuccess('Verification email sent! Please check your inbox');
      refetchStatus();
    } catch (error: any) {
      if (error?.status === 409) {
        showError('This email address is already in use');
      } else if (error?.status === 429) {
        showError('Too many requests. Please try again later');
      } else {
        showError(error?.data?.message || 'Failed to request email change');
      }
    }
    setShowConfirmDialog(false);
  };

  const handleResendVerification = async () => {
    try {
      await resendVerification().unwrap();
      showSuccess('Verification email resent! Please check your inbox');
    } catch (error: any) {
      if (error?.status === 429) {
        showError('Too many requests. Please try again later');
      } else {
        showError(error?.data?.message || 'Failed to resend verification email');
      }
    }
  };

  const handleCancelChange = async () => {
    try {
      await cancelEmailChange().unwrap();
      showSuccess('Email change cancelled successfully');
      refetchStatus();
      onClose();
    } catch (error: any) {
      showError(error?.data?.message || 'Failed to cancel email change');
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="email-change-modal-overlay" onClick={onClose}>
      <div className="email-change-modal" onClick={(e) => e.stopPropagation()}>
        <div className="email-change-modal-header">
          <h2 className="email-change-modal-title">Change Email Address</h2>
          <button className="email-change-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        {showConfirmDialog ? (
          <div className="email-change-modal-body">
            <div className="email-change-confirm">
              <h3>Confirm Email Change</h3>
              <p className="confirm-message">
                Are you sure you want to change your email address from:
              </p>
              <div className="email-display current-email">{currentEmail}</div>
              <p className="confirm-message">to:</p>
              <div className="email-display new-email">{newEmail}</div>
              <p className="warning-message">
                You'll need to verify your new email address before it becomes active.
                A verification link will be sent to your new email.
              </p>
            </div>
            <div className="email-change-modal-footer">
              <MagicButton
                label="Cancel"
                color="dark-grey"
                onPress={() => setShowConfirmDialog(false)}
                isDisabled={isRequesting}
              />
              <MagicButton
                label="Confirm Change"
                color="purple"
                onPress={handleConfirmChange}
                isLoading={isRequesting}
                isDisabled={isRequesting}
              />
            </div>
          </div>
        ) : confirmationVisible ? (
          <div className="email-change-modal-body">
            <div className="email-change-success">
              <div className="email-change-success-icon">✓</div>
              <h3>Verification Email Sent!</h3>
              <p>
                We've sent a verification link to <strong>{newEmail}</strong>.
              </p>
              <p className="email-change-note">
                Please check your email and click the verification link to complete the email change.
                The link will expire in 7 days.
              </p>
              <p className="email-change-note">
                Your current email address will remain active until you verify the new one.
              </p>
            </div>
          </div>
        ) : emailChangeStatus?.pendingEmail ? (
          <div className="email-change-modal-body">
            <div className="pending-change-info">
              <h3>Pending Email Change</h3>
              <p>You have a pending email change request:</p>
              <div className="pending-details">
                <div className="detail-row">
                  <span className="detail-label">Current Email:</span>
                  <span className="detail-value">{currentEmail}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">New Email:</span>
                  <span className="detail-value">{emailChangeStatus.pendingEmail}</span>
                </div>
                {emailChangeStatus.requestedAt && (
                  <div className="detail-row">
                    <span className="detail-label">Requested:</span>
                    <span className="detail-value">{formatDate(emailChangeStatus.requestedAt)}</span>
                  </div>
                )}
                {emailChangeStatus.expiresAt && (
                  <div className="detail-row">
                    <span className="detail-label">Expires:</span>
                    <span className="detail-value">{formatDate(emailChangeStatus.expiresAt)}</span>
                  </div>
                )}
              </div>
              <p className="pending-note">
                Check your email for the verification link. If you didn't receive it, you can resend it below.
              </p>
            </div>
            <div className="email-change-modal-footer">
              <MagicButton
                label="Cancel Change"
                color="dark-grey"
                onPress={handleCancelChange}
                isLoading={isCancelling}
                isDisabled={isResending || isCancelling}
              />
              <MagicButton
                label="Resend Verification"
                color="purple"
                onPress={handleResendVerification}
                isLoading={isResending}
                isDisabled={isResending || isCancelling}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="email-change-modal-body">
              <div className="current-email-info">
                <span className="current-email-label">Current Email:</span>
                <span className="current-email-value">{currentEmail}</span>
              </div>
              <p className="email-change-description">
                Enter your new email address below. We'll send a verification link to confirm the change.
              </p>
              <TextInputField
                label="New Email Address"
                placeholder="Enter new email address"
                value={newEmail}
                error={emailError}
                onChange={(value) => {
                  setNewEmail(value);
                  if (emailError) setEmailError(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newEmail) handleSubmit();
                }}
              />
            </div>

            <div className="email-change-modal-footer">
              <MagicButton
                label="Change Email"
                color="purple"
                onPress={handleSubmit}
                isLoading={isRequesting}
                isDisabled={!newEmail || isRequesting}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default EmailChangeModal;